import { useEffect, useState } from 'react';
import { graphqlApi } from '../services/graphql';

/**
 * Application counters displayed on a brand/agency campaign card.
 *
 * - applied: matches with status === 'applied' (인플루언서가 1차 지원)
 * - pendingApproval: matches with status in {'shortlisted', 'requested'}
 *   — host has shortlisted the applicant and is waiting on the influencer
 *   to accept (or has sent an offer awaiting acceptance).
 * - confirmed: matches with status in {'accepted', 'completed'}
 *   — influencer accepted; campaign is in progress or done.
 */
export interface CampaignApplicationStats {
  applied: number;
  pendingApproval: number;
  confirmed: number;
}

export type CampaignApplicationStatsMap = Record<string, CampaignApplicationStats>;

const EMPTY: CampaignApplicationStatsMap = {};

/**
 * Fetch all applications for this host once (GET /getHostApplications) and
 * return a per-campaignId aggregate. Avoids N-per-card network calls on the
 * dashboard grid.
 *
 * Returns an empty map while loading or if the host has no hostId, so
 * consumers can render conditionally without null checks.
 */
export function useCampaignApplicationStats(
  hostId: string | null | undefined,
): CampaignApplicationStatsMap {
  const [stats, setStats] = useState<CampaignApplicationStatsMap>(EMPTY);

  useEffect(() => {
    if (!hostId) {
      setStats(EMPTY);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const resp = await graphqlApi.getHostApplications(hostId);
        if (cancelled) return;

        const byCampaign: CampaignApplicationStatsMap = {};
        const matches = resp.success ? resp.data?.matches ?? [] : [];

        for (const m of matches) {
          const cid = (m as any).campaignId;
          if (!cid) continue;
          const bucket = byCampaign[cid] ?? {
            applied: 0,
            pendingApproval: 0,
            confirmed: 0,
          };
          switch ((m as any).status) {
            case 'applied':
              bucket.applied += 1;
              break;
            case 'shortlisted':
            case 'requested':
              bucket.pendingApproval += 1;
              break;
            case 'accepted':
            case 'completed':
              bucket.confirmed += 1;
              break;
            default:
              // 'rejected', 'cancelled', 'expired' — intentionally not counted.
              break;
          }
          byCampaign[cid] = bucket;
        }

        setStats(byCampaign);
      } catch (err) {
        // Non-fatal — the card falls back to hiding the counters.
        console.warn('[useCampaignApplicationStats] fetch failed:', err);
        if (!cancelled) setStats(EMPTY);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hostId]);

  return stats;
}
